import { useState } from 'react';
import type {
  SurveyDefinition,
  IdentitySchema,
  IdentityField,
  Identity,
} from '@webforge-survey/shared';

interface IdentityScreenProps {
  definition: SurveyDefinition;
  /** Identité existante (re-hydratée). */
  initial?: Identity;
  /** Appelé avec l'identité validée + opt-in choisi. */
  onContinue: (identity: Identity, optIn: boolean) => void;
  /** Mode 'optional' = post-questions, opt-in libre.
   *  Mode 'required' = pre-questions, identité obligatoire. */
  mode: 'optional' | 'required';
}

const DEFAULT_SCHEMAS: Record<string, IdentitySchema> = {
  minimal: {
    template: 'minimal',
    fields: [
      { name: 'prenom', label: 'Prénom', type: 'text', required: true },
      { name: 'email', label: 'Adresse e-mail', type: 'email', required: true },
    ],
  },
  email: {
    template: 'email',
    fields: [{ name: 'email', label: 'Adresse e-mail', type: 'email', required: true }],
  },
  extended: {
    template: 'extended',
    fields: [
      { name: 'prenom', label: 'Prénom', type: 'text', required: true },
      { name: 'nom', label: 'Nom', type: 'text', required: false },
      { name: 'email', label: 'Adresse e-mail', type: 'email', required: true },
      { name: 'organisation', label: 'Organisation', type: 'text', required: false },
    ],
  },
};

function resolveSchema(def: SurveyDefinition): IdentitySchema {
  if (def.identitySchema?.fields && def.identitySchema.fields.length > 0) {
    return def.identitySchema;
  }
  const tpl = def.identitySchema?.template ?? 'minimal';
  return DEFAULT_SCHEMAS[tpl] ?? DEFAULT_SCHEMAS['minimal']!;
}

function validateField(field: IdentityField, value: string): string | null {
  if (field.required && value.trim() === '') return 'Ce champ est requis.';
  if (field.type === 'email' && value !== '') {
    // Validation simple, non bloquante au point de gêner — le serveur tranche.
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!ok) return "Cette adresse e-mail ne semble pas valide.";
  }
  if (field.type === 'integer' && value !== '') {
    if (!/^-?\d+$/.test(value)) return 'Veuillez saisir un nombre entier.';
  }
  return null;
}

/**
 * Écran d'identité — RGPD_PLAN §2.2 (optional) et §2.3 (required).
 *
 * Optional : tickbox NON pré-coché ; les deux blocs visuels (avec/sans
 * identité) ont un poids strictement équivalent ; aucun nudge.
 *
 * Required : pas de tickbox optionnel ; consentement spécifique à
 * l'identification AVANT le formulaire.
 */
export function IdentityScreen({
  definition,
  initial,
  onContinue,
  mode,
}: IdentityScreenProps) {
  const schema = resolveSchema(definition);
  const fields = schema.fields ?? [];

  const [optIn, setOptIn] = useState<boolean>(false); // jamais pré-coché en optional
  const [identityConsent, setIdentityConsent] = useState<boolean>(false); // required uniquement
  const [values, setValues] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const f of fields) {
      const v = initial?.[f.name];
      out[f.name] = v === undefined || v === null ? '' : String(v);
    }
    return out;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateAll = (): boolean => {
    const errs: Record<string, string> = {};
    for (const f of fields) {
      const err = validateField(f, values[f.name] ?? '');
      if (err) errs[f.name] = err;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (mode === 'required') {
      if (!identityConsent) return;
      if (!validateAll()) return;
      const id: Identity = {};
      for (const f of fields) {
        const v = values[f.name] ?? '';
        if (v !== '') (id as Record<string, string | number>)[f.name] = v;
      }
      onContinue(id, true);
      return;
    }
    // optional
    if (!optIn) {
      onContinue(null, false);
      return;
    }
    if (!validateAll()) return;
    const id: Identity = {};
    for (const f of fields) {
      const v = values[f.name] ?? '';
      if (v !== '') (id as Record<string, string | number>)[f.name] = v;
    }
    onContinue(id, true);
  };

  if (mode === 'required') {
    return (
      <main
        className="min-h-screen bg-bg py-12 px-4"
        aria-labelledby="identity-required-title"
      >
        <div className="card max-w-prose mx-auto space-y-6">
          <header>
            <p className="text-sm text-text-muted">Identification requise</p>
            <h1 id="identity-required-title" className="mt-1">
              Avant de répondre
            </h1>
          </header>
          <div className="info-callout">
            <p>
              Cette enquête nécessite votre identification pour traiter vos
              réponses. Vos données seront chiffrées et conservées
              séparément des réponses.
            </p>
          </div>
          <fieldset>
            <legend className="sr-only">Consentement à l'identification</legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={identityConsent}
                onChange={(e) => setIdentityConsent(e.target.checked)}
                className="mt-1 h-5 w-5 accent-accent flex-shrink-0"
              />
              <span>
                {definition.identitySchema?.template
                  ? `Je consens à fournir mon identité au responsable de cette étude (champs ci-dessous).`
                  : `Je consens à fournir mon identité au responsable de cette étude.`}
              </span>
            </label>
          </fieldset>
          {identityConsent && (
            <IdentityFieldsForm
              fields={fields}
              values={values}
              errors={errors}
              onChange={(name, v) => setValues((s) => ({ ...s, [name]: v }))}
            />
          )}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              className="btn-primary flex-1"
              disabled={!identityConsent}
              onClick={handleSubmit}
            >
              Continuer
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ----- Mode optional (opt-in fin de parcours) -----
  return (
    <main
      className="min-h-screen bg-bg py-12 px-4"
      aria-labelledby="identity-optional-title"
    >
      <div className="card max-w-prose mx-auto space-y-6">
        <header>
          <p className="text-sm text-text-muted">Dernière étape avant l'envoi</p>
          <h1 id="identity-optional-title" className="mt-1">
            Souhaitez-vous associer votre identité&nbsp;?
          </h1>
        </header>

        <p className="text-text-muted">
          Vous pouvez choisir de rester anonyme (par défaut) ou d'associer
          votre identité. Les deux choix sont également valides ; nous ne vous
          incitons à aucun.
        </p>

        {/* Deux blocs visuellement équivalents (UI_DESIGN §5.3) */}
        <div className="grid sm:grid-cols-2 gap-4">
          <section
            className="rounded-lg border border-surface-2 bg-surface p-4 space-y-2"
            aria-label="Sans identité"
          >
            <h2 className="text-base font-medium">Sans identité</h2>
            <p className="text-sm text-text-muted">
              Mes réponses restent anonymes. Aucun moyen de me recontacter,
              aucun droit individuel applicable, aucune rétractation possible
              après envoi.
            </p>
          </section>
          <section
            className="rounded-lg border border-surface-2 bg-surface p-4 space-y-2"
            aria-label="Avec identité"
          >
            <h2 className="text-base font-medium">Avec identité</h2>
            <p className="text-sm text-text-muted">
              Le contrôleur pourra m'identifier. Je peux à tout moment demander
              accès, rectification, effacement de mes données auprès de lui.
            </p>
          </section>
        </div>

        <fieldset>
          <legend className="sr-only">Choix d'identification</legend>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="mt-1 h-5 w-5 accent-accent flex-shrink-0"
            />
            <span>
              Je souhaite associer mon identité à mes réponses (facultatif).
            </span>
          </label>
        </fieldset>

        {optIn && (
          <IdentityFieldsForm
            fields={fields}
            values={values}
            errors={errors}
            onChange={(name, v) => setValues((s) => ({ ...s, [name]: v }))}
          />
        )}

        {!optIn && (
          <p className="text-sm text-text-muted">
            Si vous ne cochez pas, aucune donnée d'identité n'est enregistrée.
            La dissociation est définitive.
          </p>
        )}

        <div className="pt-2">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={handleSubmit}
          >
            Envoyer mes réponses
          </button>
        </div>
      </div>
    </main>
  );
}

interface IdentityFieldsFormProps {
  fields: IdentityField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (name: string, value: string) => void;
}

function IdentityFieldsForm({
  fields,
  values,
  errors,
  onChange,
}: IdentityFieldsFormProps) {
  return (
    <div className="space-y-4">
      {fields.map((f) => {
        const id = `id-${f.name}`;
        const errId = `${id}-err`;
        const err = errors[f.name];
        return (
          <div key={f.name} className="space-y-1">
            <label htmlFor={id} className="field-label">
              {f.label}
              {f.required && (
                <span className="text-text-muted text-sm font-normal ml-1">
                  (requis)
                </span>
              )}
            </label>
            <input
              id={id}
              type={f.type === 'email' ? 'email' : f.type === 'integer' ? 'number' : 'text'}
              className="input-base"
              value={values[f.name] ?? ''}
              onChange={(e) => onChange(f.name, e.target.value)}
              aria-required={f.required || undefined}
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? errId : undefined}
              autoComplete={f.type === 'email' ? 'email' : 'off'}
            />
            {err && (
              <p id={errId} role="alert" className="field-error">
                <span aria-hidden="true">⚠</span>
                <span>{err}</span>
              </p>
            )}
          </div>
        );
      })}
      <p className="text-xs text-text-muted">
        Les champs d'identité sont chiffrés et stockés séparément des réponses.
      </p>
    </div>
  );
}
